import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FiFilter, FiX, FiGrid, FiChevronRight, FiSearch, FiStar } from 'react-icons/fi';
import ProductCard from '../components/ui/ProductCard';
import Spinner from '../components/ui/Spinner';
import API from '../api/axios';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');

  const page = parseInt(searchParams.get('page') || '1');
  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || '';
  const featured = searchParams.get('featured') || '';
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';

  // Sync local price state with URL
  useEffect(() => {
    setPriceMin(minPrice);
    setPriceMax(maxPrice);
  }, [minPrice, maxPrice]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (category) params.set('category', category);
      if (search) params.set('search', search);
      if (sort) params.set('sort', sort);
      if (featured) params.set('featured', featured);
      if (minPrice) params.set('minPrice', minPrice);
      if (maxPrice) params.set('maxPrice', maxPrice);

      const { data } = await API.get(`/products?${params}`);
      setProducts(data.products);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch {
      // fail gracefully
    } finally {
      setLoading(false);
    }
  }, [page, category, search, sort, featured, minPrice, maxPrice]);

  useEffect(() => {
    API.get('/categories').then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.set('page', '1');
    setSearchParams(p);
  };

  const applyPrice = () => {
    const p = new URLSearchParams(searchParams);
    if (priceMin) p.set('minPrice', priceMin); else p.delete('minPrice');
    if (priceMax) p.set('maxPrice', priceMax); else p.delete('maxPrice');
    p.set('page', '1');
    setSearchParams(p);
  };

  const setPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', p);
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setPriceMin('');
    setPriceMax('');
    setSearchParams({});
  };

  const activeCategory = categories.find((c) => c._id === category);
  const hasFilters = category || search || sort || minPrice || maxPrice || featured;

  const FilterContent = () => (
    <div className="space-y-7">
      {/* Featured toggle */}
      <div>
        <button
          onClick={() => setParam('featured', featured ? '' : 'true')}
          className={`w-full flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
            featured
              ? 'border-accent-500 bg-accent-50 text-accent-700'
              : 'border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-700'
          }`}
        >
          <FiStar size={15} className={featured ? 'fill-accent-500 text-accent-500' : ''} />
          Featured Products
          {featured && <span className="ml-auto text-xs bg-accent-500 text-white px-2 py-0.5 rounded-full">ON</span>}
        </button>
      </div>

      {/* Categories */}
      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Categories</h3>
        <ul className="space-y-0.5">
          <li>
            <button
              onClick={() => setParam('category', '')}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${
                !category
                  ? 'bg-primary-700 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span>All Categories</span>
              {!category && <FiChevronRight size={13} />}
            </button>
          </li>
          {categories.map((cat) => (
            <li key={cat._id}>
              <button
                onClick={() => setParam('category', cat._id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${
                  category === cat._id
                    ? 'bg-primary-700 text-white shadow-sm'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="truncate">{cat.name}</span>
                {category === cat._id && <FiChevronRight size={13} />}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Price Range (PKR)</h3>
        <div className="flex gap-2 mb-3">
          <input
            type="number"
            placeholder="Min"
            value={priceMin}
            onChange={(e) => setPriceMin(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <span className="flex items-center text-gray-300 font-light">—</span>
          <input
            type="number"
            placeholder="Max"
            value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={applyPrice}
          className="w-full py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
        >
          Apply
        </button>
      </div>

      {/* Clear */}
      {hasFilters && (
        <button
          onClick={clearFilters}
          className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 font-medium transition-colors"
        >
          <FiX size={14} /> Clear all filters
        </button>
      )}
    </div>
  );

  // Active filter chips
  const activeChips = [];
  if (search) activeChips.push({ label: `"${search}"`, key: 'search' });
  if (featured) activeChips.push({ label: 'Featured', key: 'featured' });
  if (activeCategory) activeChips.push({ label: activeCategory.name, key: 'category' });
  if (minPrice || maxPrice) activeChips.push({ label: `PKR ${minPrice || '0'} – ${maxPrice || '∞'}`, key: '_price' });

  const removeChip = (key) => {
    if (key === '_price') {
      const p = new URLSearchParams(searchParams);
      p.delete('minPrice');
      p.delete('maxPrice');
      setPriceMin('');
      setPriceMax('');
      setSearchParams(p);
    } else {
      setParam(key, '');
    }
  };

  return (
    <div className="bg-gray-50/60 min-h-screen">
      <div className="container-custom py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
          <FiChevronRight size={12} className="text-gray-300" />
          <span className="text-gray-800 font-semibold">Shop</span>
          {search && (
            <>
              <FiChevronRight size={12} className="text-gray-300" />
              <span className="text-gray-600">Search: "{search}"</span>
            </>
          )}
          {activeCategory && (
            <>
              <FiChevronRight size={12} className="text-gray-300" />
              <span className="text-gray-600">{activeCategory.name}</span>
            </>
          )}
        </nav>

        <div className="flex gap-8 items-start">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-24">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-bold text-gray-800 text-base">Filters</h2>
                {hasFilters && (
                  <button onClick={clearFilters} className="text-xs text-red-400 hover:text-red-600 font-medium">
                    Reset
                  </button>
                )}
              </div>
              <FilterContent />
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setFilterOpen(true)}
                  className="lg:hidden flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  <FiFilter size={14} /> Filters
                  {hasFilters && <span className="w-5 h-5 bg-primary-600 text-white rounded-full text-xs flex items-center justify-center font-bold">{activeChips.length}</span>}
                </button>
                <p className="text-sm text-gray-500">
                  {loading ? (
                    <span className="inline-block w-20 h-4 bg-gray-100 rounded animate-pulse" />
                  ) : (
                    <><span className="font-bold text-gray-800">{total}</span> product{total !== 1 ? 's' : ''} found</>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-400 font-medium">Sort by</label>
                <select
                  value={sort}
                  onChange={(e) => setParam('sort', e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">Default</option>
                  <option value="latest">Latest First</option>
                  <option value="popular">Most Popular</option>
                  <option value="price_asc">Price: Low → High</option>
                  <option value="price_desc">Price: High → Low</option>
                </select>
              </div>
            </div>

            {/* Active filter chips */}
            {activeChips.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {activeChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1.5 bg-primary-50 text-primary-700 border border-primary-100 rounded-full px-3 py-1 text-xs font-semibold"
                  >
                    {chip.label}
                    <button onClick={() => removeChip(chip.key)} className="hover:text-primary-900 transition-colors">
                      <FiX size={11} />
                    </button>
                  </span>
                ))}
                <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-red-500 font-medium px-1 transition-colors">
                  Clear all
                </button>
              </div>
            )}

            {/* Products grid */}
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Spinner />
              </div>
            ) : products.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm text-center py-20 px-8">
                <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <FiSearch size={32} className="text-gray-300" />
                </div>
                <p className="text-lg font-bold text-gray-700 mb-2">No products found</p>
                <p className="text-sm text-gray-400 mb-6">Try adjusting your filters or searching with different keywords.</p>
                <button onClick={clearFilters} className="btn-primary">Clear Filters</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {products.map((p) => <ProductCard key={p._id} product={p} />)}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && !loading && (
              <div className="flex justify-center items-center gap-1.5 mt-10">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed bg-white transition-colors"
                >
                  Previous
                </button>
                {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 rounded-xl text-sm font-semibold transition-all ${
                        p === page
                          ? 'bg-primary-700 text-white shadow-sm'
                          : 'border border-gray-200 hover:bg-gray-50 text-gray-700 bg-white'
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed bg-white transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile filter drawer */}
        {filterOpen && (
          <div className="fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setFilterOpen(false)} />
            <div className="relative bg-white w-80 max-w-full h-full overflow-y-auto p-6 shadow-2xl ml-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-bold text-lg text-gray-800">Filters</h2>
                <button onClick={() => setFilterOpen(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                  <FiX size={18} />
                </button>
              </div>
              <FilterContent />
              <div className="mt-8">
                <button
                  onClick={() => setFilterOpen(false)}
                  className="w-full py-3 bg-primary-700 text-white font-bold rounded-xl hover:bg-primary-800 transition-colors"
                >
                  Show Results ({total})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
